import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid, Column, FormGroup, Tile,
  Stack, ToastNotification, 
  //DatePicker, DatePickerInput,
  TextInput,
  TextArea, Checkbox,
} from "@carbon/react";
import CommonFields from "../../common/CommonField";

const RentalDetails = ({
  propertyUuid = "", data = {}, attributeValues = [], formMode = "add", currentStep = 3,
  lastSavedStep = 0, onStepChange, createProperty, saveSectionAttributes,
  onSaveSuccess, onComplete, onSaved, onBack, CommonButtons, onNavigateNext,
}) => {
  // -----------------------------
  // DEBUG: Log received props
  // -----------------------------
  console.log('🔍 RentalDetails (Step 3) received:', {
    formMode,
    currentStep,
    lastSavedStep,
    isEditForm: formMode === "edit" || lastSavedStep >= currentStep,
    hasPropertyUuid: !!propertyUuid,
    hasData: !!data && Object.keys(data).length > 0,
    hasAttributes: !!data?.attributes,
    attributesKeys: data?.attributes ? Object.keys(data.attributes) : []
  });

  // -----------------------------
  // CHILD EDIT MODE CALCULATION
  // -----------------------------
  const isEditForm = useMemo(() => {
    return formMode === "edit" || lastSavedStep >= currentStep;
  }, [formMode, lastSavedStep, currentStep]);

  // -----------------------------
  // FIELD MAP - Consistent pattern
  // -----------------------------
  const FIELD_MAP = useMemo(() => ({
    expectedRent: "flat_expected_rent",
    rentNegotiable: "flat_rent_negotiable",
    expectedDeposit: "flat_expected_deposit",
    monthlyMaintenance: "rent_monthly_maintenance",
    maintenanceAmount: "flat_maintenance_amount",
    availableFrom: "flat_available_from",
    furnishing: "flat_furnishing",
    parking: "flat_parking",
    description: "flat_description",
  }), []);

  // -----------------------------
  // REQUIRED FIELDS - Moved inside useMemo
  // -----------------------------
  const requiredFields = useMemo(() => [
    "expectedRent",
    "expectedDeposit",
    "monthlyMaintenance",
    "furnishing",
    "parking",
  ], []);

  // -----------------------------
  // STATE
  // -----------------------------
  const [form, setForm] = useState({
    expectedRent: "",
    rentNegotiable: false,
    expectedDeposit: "",
    monthlyMaintenance: "",
    maintenanceAmount: "",
    availableFrom: "",
    furnishing: "",
    parking: "",
    description: ""
  });

  const [touched, setTouched] = useState({});
  const [submitAttempt, setSubmitAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // -----------------------------
  // API OPTIONS ONLY
  // -----------------------------
  const getOptions = useCallback(
    (fieldKey) => {
      const apiKey = FIELD_MAP[fieldKey];
      if (!apiKey) return [];

      return attributeValues
        .filter((a) => a.attrName === apiKey)
        .map((a) => ({
          id: a.id,
          label: a.attr_val_name || a.label || a.value,
        }));
    },
    [attributeValues, FIELD_MAP]
  );

  // -----------------------------
  // FIELD UPDATES
  // -----------------------------
  const updateField = useCallback((key, value) => {
    console.log(`🔄 Updating field ${key} to:`, value);
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));

    // Clear errors for this field when user starts typing
    setError("");
  }, []);

  // -----------------------------
  // LOAD INITIAL DATA (EDIT) - Consistent pattern
  // -----------------------------
  useEffect(() => {
    if (!data?.attributes) {
      console.log('💧 No attributes data found in RentalDetails');
      return;
    }

    console.log('💧 RentalDetails hydrating form with data:', data.attributes);

    const incoming = {};

    Object.entries(FIELD_MAP).forEach(([field, apiKey]) => {
      const raw = data.attributes[apiKey];
      if (raw == null) {
        console.log(`  - ${field}: No data for ${apiKey}`);
        return;
      }

      console.log(`  - ${field}: raw value =`, raw, 'type:', typeof raw);

      // Handle boolean field
      if (field === "rentNegotiable") {
        incoming[field] = raw === true || raw === "true" || raw === 1 || raw === "1" || raw === "yes";
      } else {
        // Convert all other values to string
        incoming[field] = String(raw);
      }
    });

    console.log('💧 RentalDetails incoming form data:', incoming);
    setForm((prev) => {
      const newForm = { ...prev, ...incoming };
      console.log('💧 RentalDetails new form state:', newForm);
      return newForm;
    });
  }, [data, FIELD_MAP]);

  // Check if maintenance amount field should be shown
  const showMaintenanceAmount = useMemo(() => {
    if (!form.monthlyMaintenance) return false;

    const selectedMaintenance = getOptions("monthlyMaintenance").find(
      (it) => String(it.id) === String(form.monthlyMaintenance)
    );

    return selectedMaintenance && selectedMaintenance.label.toLowerCase().includes("extra");
  }, [form.monthlyMaintenance, getOptions]);

  // -----------------------------
  // VALIDATION
  // -----------------------------
  const getFieldError = useCallback(
    (field) => {
      const value = form[field];
      const isTouched = touched[field];
      const hasSubmitted = submitAttempt > 0;

      // Only show errors if field has been touched or form has been submitted
      if (!isTouched && !hasSubmitted) {
        return null;
      }

      if (field === "expectedRent") {
        if (!value || value.toString().trim() === "") {
          return "Expected Rent is required";
        }
        if (Number(value) <= 0) {
          return "Expected Rent must be greater than 0";
        }
        if (isNaN(Number(value))) {
          return "Expected Rent must be a valid number";
        }
      }

      if (field === "expectedDeposit") {
        if (!value || value.toString().trim() === "") {
          return "Expected Deposit is required";
        }
        if (Number(value) <= 0) {
          return "Expected Deposit must be greater than 0";
        }
        if (isNaN(Number(value))) {
          return "Expected Deposit must be a valid number";
        }
      }

      if (field === "monthlyMaintenance") {
        if (!value || value.toString().trim() === "") {
          return "Monthly Maintenance is required";
        }
      }

      if (field === "maintenanceAmount") {
        if (showMaintenanceAmount) {
          if (!value || value.toString().trim() === "") {
            return "Maintenance Amount is required when Monthly Maintenance is Extra";
          }
          if (Number(value) <= 0) {
            return "Maintenance Amount must be greater than 0";
          }
          if (isNaN(Number(value))) {
            return "Maintenance Amount must be a valid number";
          }
        }
      }


      if (field === "furnishing") {
        if (!value || value.toString().trim() === "") {
          return "Furnishing is required";
        }
      }

      if (field === "parking") {
        if (!value || value.toString().trim() === "") {
          return "Parking is required";
        }
      }

      return null;
    },
    [form, touched, submitAttempt, showMaintenanceAmount]
  );

  // -----------------------------
  // USE COMMON FIELDS
  // -----------------------------
  const { renderDropdown, renderNumberField } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur: (field) => {
      setTouched((prev) => ({ ...prev, [field]: true }));
    },
    updateField,
    getOptions,
  });

  // -----------------------------
  // SAVE - Consistent pattern
  // -----------------------------
  const handleSave = useCallback(async (navigateNext = false) => {
    setSubmitAttempt((prev) => prev + 1);
    console.log('💾 RentalDetails handleSave:', {
      navigateNext,
      isEditForm,
      currentStep,
      hasPropertyUuid: !!propertyUuid,
      formData: form
    });

    // Mark all fields as touched to show validation errors
    const allFields = [...requiredFields];
    if (showMaintenanceAmount) {
      allFields.push("maintenanceAmount");
    }

    setTouched(prev => {
      const newTouched = { ...prev };
      allFields.forEach(field => {
        newTouched[field] = true;
      });
      return newTouched;
    });
    await new Promise(resolve => setTimeout(resolve, 0));
    let hasValidationErrors = false;
    const errors = [];

    allFields.forEach(field => {
      const value = form[field];

      if (field === "expectedRent" || field === "expectedDeposit") {
        if (!value || value.toString().trim() === "") {
          hasValidationErrors = true;
          errors.push(`${field} is required`);
        } else if (Number(value) <= 0) {
          hasValidationErrors = true;
          errors.push(`${field} must be greater than 0`);
        } else if (isNaN(Number(value))) {
          hasValidationErrors = true;
          errors.push(`${field} must be a valid number`);
        }
      }
      else if (field === "maintenanceAmount") {
        if (showMaintenanceAmount) {
          if (!value || value.toString().trim() === "") {
            hasValidationErrors = true;
            errors.push("Maintenance Amount is required");
          } else if (Number(value) <= 0) {
            hasValidationErrors = true;
            errors.push("Maintenance Amount must be greater than 0");
          } else if (isNaN(Number(value))) {
            hasValidationErrors = true;
            errors.push("Maintenance Amount must be a valid number");
          }
        }
      }
      else {
        if (!value || value.toString().trim() === "") {
          hasValidationErrors = true;
          errors.push(`${field} is required`);
        }
      }
    });

    if (hasValidationErrors) {
      setError("Please fill all required fields correctly");
      setSaving(false);
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      let uuid = propertyUuid;

      // Create property if it doesn't exist (should already exist from Step 1)
      if (!uuid && createProperty) {
        console.log('🆕 Creating property from Step 3...');
        uuid = await createProperty();
        onSaveSuccess?.(uuid, form, currentStep);
      }

      const payload = {};

      for (const [field, apiKey] of Object.entries(FIELD_MAP)) {
        const v = form[field];
        if (v == null || v === "") continue;

        // Handle boolean field
        if (field === "rentNegotiable") {
          payload[apiKey] = v ? "yes" : "no";
        }
        // Handle numeric fields
        else if (field === "expectedRent" || field === "expectedDeposit" || field === "maintenanceAmount") {
          payload[apiKey] = Number(v);
        }
        // Handle all other fields
        else if (field === "monthlyMaintenance" || field === "furnishing" || field === "parking") {
          // Dropdown fields - send as numbers
          payload[apiKey] = Number(v);
        }
        // Handle text fields
        else {
          payload[apiKey] = v;
        }
      }

      console.log('💾 Saving rental details:', payload);
      await saveSectionAttributes(uuid, payload, form, "rental_details");

      setSuccess("Rental details saved successfully!");

      // Call parent's onSaveSuccess with step number
      onSaveSuccess?.(uuid, form, currentStep);

      // Call onComplete if provided
      onComplete?.(form);

      // Navigate to next step if requested
      if (navigateNext) {
        console.log('➡️ Navigating to next step after save');
        onNavigateNext?.(form);
      }
    } catch (err) {
      console.error('❌ Save failed:', err);
      setError(err.message || "Failed to save rental details");
    } finally {
      setSaving(false);
    }
  }, [
    form,
    propertyUuid,
    currentStep,
    isEditForm,
    requiredFields,
    showMaintenanceAmount,
    FIELD_MAP,
    createProperty,
    saveSectionAttributes,
    onSaveSuccess,
    onComplete,
    onNavigateNext,
  ]);

  // -----------------------------
  // BUTTON HANDLERS
  // -----------------------------
  const handleSaveOnly = useCallback(() => {
   
    handleSave(false);
  }, [handleSave]);

  const handleSaveAndNext = useCallback(() => {
    
    handleSave(true);
     
  }, [handleSave]);

  // -----------------------------
  // CHECKBOX COMPONENT
  // -----------------------------
  const renderCheckbox = useCallback((fieldKey, label) => {
    return (
      <Checkbox
        id={fieldKey}
        labelText={label}
        checked={form[fieldKey] || false}
        onChange={(e) => updateField(fieldKey, e.target.checked)}
      />
    );
  }, [form, updateField]);

  // -----------------------------
  // RENDER UI
  // -----------------------------
  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          timeout={4000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          timeout={3000}
          lowContrast
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <Grid>
        <Column sm={4} md={8} lg={16}>
          <Tile style={{ padding: "2rem", backgroundColor: "white" }}>
<div style={{ textAlign: "center", marginBottom: "1rem" }}>
 
 

  <p
    style={{
      color: "#0f62fe",
      fontSize: "0.95rem",
      fontWeight: 500,
      maxWidth: "700px",
      margin: "0 auto",
      lineHeight: "1.5",
    }}
  >
    Provide rental details about your property to help tenants better understand your listing.
  </p>
</div>            <Stack gap={6}>

              {/* EXPECTED RENT & DEPOSIT */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderNumberField("expectedRent", "Expected Rent", "Enter amount", true)}
                  </Column>
                  <Column lg={8}>
                    {renderNumberField("expectedDeposit", "Expected Deposit", "Enter amount", true)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* RENT NEGOTIABLE */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderCheckbox("rentNegotiable", "Rent Negotiable")}
                  </Column>
                </Grid>
              </FormGroup>

              {/* MONTHLY MAINTENANCE */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderDropdown("monthlyMaintenance", "Monthly Maintenance", true)}
                  </Column>
                  <Column lg={8}>
                    {showMaintenanceAmount && (
                      renderNumberField("maintenanceAmount", "Maintenance Amount (₹/month)", "Enter amount", true)
                    )}
                  </Column>
                </Grid>
              </FormGroup>
{/* AVAILABLE FROM */}
<FormGroup>
  <Grid>
    <Column lg={8}>
      <TextInput
        id="availableFrom"
        type="date"
        labelText="Available From"
        value={form.availableFrom || ""}
        onChange={(e) => updateField("availableFrom", e.target.value)}
        onBlur={() =>
          setTouched((prev) => ({
            ...prev,
            availableFrom: true,
          }))
        }
      />
    </Column>
  </Grid>
</FormGroup>

              {/* FURNISHING & PARKING */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderDropdown("furnishing", "Furnishing", true)}
                  </Column>
                  <Column lg={8}>
                    {renderDropdown("parking", "Parking", true)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* DESCRIPTION */}
              <FormGroup>
                <Grid>
                  <Column lg={16} md={3}>
                    <TextArea
                      id="description"
                      labelText="Description"
                      value={form.description || ""}
                      onChange={(e) => updateField("description", e.target.value)}
                      onBlur={() => setTouched(prev => ({ ...prev, description: true }))}
                      placeholder="Write a few lines about your property (do not mention contact information)"
                      rows={4}
                      invalid={false}
                      invalidText=""
                    />
                  </Column>
                </Grid>
              </FormGroup>

              {/* BUTTONS */}
              {CommonButtons && (
                <CommonButtons
                  onSave={handleSaveOnly}
                  onNext={handleSaveAndNext}
                  showSave
                  showNext
                  isSaveLoading={saving}
                  stepData={form}
                />
              )}
            </Stack>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default RentalDetails;